import {Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges} from '@angular/core';
import {FormControl, UntypedFormControl, UntypedFormGroup, Validators} from '@angular/forms';
import {PopoverControllerService} from '../../../../shared/shared-services/overlay/popover-controller.service';
import {SelectPopoverComponent} from '../../../shared-module/components/select-popover/select-popover.component';
import {ITableNavSettings} from '../../../../features/settings-new/models';

@Component({
  selector: 'app-shared-grid-table-navigation',
  templateUrl: './shared-grid-table-navigation.component.html',
  styleUrls: ['./shared-grid-table-navigation.component.scss'],
  standalone: false
})
export class SharedGridTableNavigationComponent implements OnInit, OnChanges {

  @Input() navSettings: ITableNavSettings;
  @Input() altNavName?: string;
  @Input() disabledOpts?: { [key in keyof ITableNavSettings]: boolean } =
    {cellDebounce: true} as { [key in keyof ITableNavSettings]: boolean };

  @Output() readonly settingsChange = new EventEmitter<ITableNavSettings>();


  settingsFG: UntypedFormGroup;
  saving: boolean;

  readonly endSelection = {
    'restart_page': 'jump back to first field on current page',
    'restart_column': 'restart at the top of the column',
    'next_column': 'start the next column',
    'next_page': 'go to first field on the next page',
    'next_page_same_column': 'continue the column on the next page',
    'restart_row': 'restart at the beginning of the current row'
  };

  private readonly defaultSetting: ITableNavSettings = {tabEnd: 'restart_page', enterEnd: 'restart_column'};

  constructor(
    private popControl: PopoverControllerService
  ) {
  }

  ngOnInit(): void {
    if (!this.navSettings) {
      this.navSettings = {} as ITableNavSettings;
      Object.keys(this.defaultSetting)
        .forEach((key: string) => (this.navSettings[key] = this.defaultSetting[key]));
    }
    this.settingsFG = new UntypedFormGroup({
      tabEnd: new UntypedFormControl(this.navSettings.tabEnd, [Validators.required]),
      enterEnd: new UntypedFormControl(this.navSettings.enterEnd, [Validators.required]),
      cellDebounce: new FormControl(this.navSettings.cellDebounce, [Validators.min(500), Validators.max(3000)])
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['navSettings']?.currentValue) {
      const currentValue = changes['navSettings'].currentValue;
      this.updateNavData(currentValue as ITableNavSettings);
    }
  }

  updateNavData(currentValue: ITableNavSettings): void {
    this.navSettings = currentValue;
    this.settingsFG = new UntypedFormGroup({
      tabEnd: new UntypedFormControl(this.navSettings.tabEnd, [Validators.required]),
      enterEnd: new UntypedFormControl(this.navSettings.enterEnd, [Validators.required]),
      cellDebounce: new FormControl(this.navSettings.cellDebounce, [Validators.min(500), Validators.max(3000)])
    });
  }

  async selectEnd(event: Event, endOf: 'tab' | 'enter'): Promise<void> {
    let order = ['restart_page', 'restart_column', 'next_column', 'next_page', 'next_page_same_column', 'restart_row'];
    let selection = <Record<string, string>>this.endSelection;

    if (endOf === 'tab') {
      order = order.filter((key: string) => !['restart_column', 'next_column', 'next_page_same_column'].includes(key));
      selection = {};
      order.forEach((key: string) => (selection[key] = this.endSelection[key]));
    }
    if (endOf === 'enter') {
      order = order.filter((key: string) => !['restart_row'].includes(key));
      selection = {};
      order.forEach((key: string) => (selection[key] = this.endSelection[key]));
    }
    const pc = await this.popControl.create({
      component: SelectPopoverComponent, componentProps: {
        selection,
        value: this.settingsFG.get(`${endOf}End`).value,
        order,
        selectModButtons: [{
          text: 'Default', handler: (check: Record<string, boolean>): Record<string, boolean> => {
            check = {};
            check[this.defaultSetting[`${endOf}End`]] = true;
            return check;
          }
        }]
      },
      event
    });
    await pc.present();
    const {data} = await pc.onDidDismiss<string[]>();

    if (data) {
      this.settingsFG.get(`${endOf}End`).setValue(data);
    }
  }

  reset(key: keyof ITableNavSettings): void {
    switch (key) {
      case 'cellDebounce':
        this.settingsFG.get('cellDebounce').reset(null);
        break;
      case 'tabEnd':
        break;
      case 'enterEnd':
        break;
    }
  }

  onClose(): void {
    this.settingsChange.emit();
  }

  onSave(): void {
    this.settingsChange.emit(this.settingsFG.value);
  }

}
